/**
 * This component is for the Recover Device flow
 *
 * Purpose: Allows users who have lost their MFA device to log in using their
 * recovery email address.
 *
 * Flow: User clicks "Recover Account" on MFA challenge screen → receives code
 * on recovery email → enters code → logs in successfully.
 *
 */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Input } from '@wds/input';
import { Button } from '@wds/button';
import { Slide } from '@wds/motion';
import { ProgressCircle } from '@wds/progress-circle';

import { getCookie, isSafeRedirectUrl } from 'sma.common/utils';
import STRINGS from '../constants';
import MFAErrorMessage from './MFAErrorMessage';
import { clearCooldownTimer, startCooldownTimer } from '../utils/cooldown';

const RESEND_COOLDOWN_SEC = 120;

const RecoveryEmailChallenge = (props) => {
    const {
        isLoaded,
        currentPage,
        isSliding,
        setIsSliding,
        setCurrentPage,
        handleError,
        nextUrl,
    } = props;

    const [code, setCode] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [resending, setResending] = useState(false);
    const [codeAlreadyPending, setCodeAlreadyPending] = useState(false);
    const timerRef = useRef(null);
    const cooldownEndAtRef = useRef(0);

    useEffect(() => {
        return () => {
            clearCooldownTimer(timerRef);
        };
    }, []);

    // Send code when component mounts
    useEffect(() => {
        if (currentPage === 'recovery-email-challenge' && !codeSent) {
            handleSendCode();
        }
    }, [currentPage]);

    const isValidCode = code.length === 6 && /^[0-9]{6}$/.test(code);

    const startCooldown = (remaining) => {
        startCooldownTimer({
            remaining,
            defaultSeconds: RESEND_COOLDOWN_SEC,
            setCooldown,
            timerRef,
            endAtRef: cooldownEndAtRef,
        });
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setCode(value);
    };

    const handleSendCode = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await fetch(reverse('api-internal-mfa-recovery-email'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({
                    action: 'send_challenge_code',
                }),
            });

            const data = await response.json();

            if (data.success) {
                setMaskedEmail(data.masked_email);
                setCodeSent(true);
                setCodeAlreadyPending(false);
                startCooldown();
            } else {
                if ((data.error === 'cooldown_active' || data.error === 'code_already_pending') && data.time_remaining) {
                    setMaskedEmail(data.masked_email || maskedEmail);
                    setCodeSent(true);
                    setCodeAlreadyPending(true);
                    startCooldown(data.time_remaining);
                } else if (data.error === 'no_recovery_email') {
                    setError(STRINGS.noRecoveryEmailSetup || 'No recovery email is set up for this account.');
                } else {
                    setError(STRINGS?.[data.error] || data.error || STRINGS.unknownError);
                }
            }
        } catch (err) {
            setError(STRINGS.unknownError);
            handleError?.(err);
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (cooldown > 0 || resending) return;

        setResending(true);
        setError('');
        setCode('');

        try {
            const response = await fetch(reverse('api-internal-mfa-recovery-email'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({
                    action: 'send_challenge_code',
                }),
            });

            const data = await response.json();

            if (data.success) {
                setCodeAlreadyPending(false);
                startCooldown();
            } else {
                if (data.error === 'cooldown_active' && data.time_remaining) {
                    startCooldown(data.time_remaining);
                }
                setError(STRINGS?.[data.error] || data.error || STRINGS.unknownError);
            }
        } catch (err) {
            setError(STRINGS.unknownError);
            handleError?.(err);
        } finally {
            setResending(false);
        }
    };

    const handleVerifyCode = async () => {
        if (!isValidCode) {
            setError(STRINGS.invalidCode || 'Please enter a valid 6-digit code');
            return;
        }

        setLoading(true);
        setError('');
        let isNavigating = false;

        try {
            const response = await fetch(reverse('api-internal-mfa-recovery-email'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({
                    action: 'verify_challenge_code',
                    code: code,
                }),
            });

            const data = await response.json();

            if (data.success) {
                isNavigating = true;
                if (isSafeRedirectUrl(nextUrl)) {
                    const parsed = new URL(nextUrl, window.location.origin);
                    window.location.replace(parsed.pathname + parsed.search + parsed.hash);
                } else {
                    window.location.replace('/');
                }
                return;
            } else {
                if (data.error === 'invalid_code_locked_out') {
                    isNavigating = true;
                    window.location.href = reverse('mfa-lockout');
                    return;
                }
                setError(STRINGS?.[data.error] || data.error || STRINGS.invalidCode);
                setCode('');
            }
        } catch (err) {
            setError(STRINGS.unknownError);
            handleError?.(err);
        } finally {
            if (!isNavigating) {
                setLoading(false);
            }
        }
    };

    return (
        <Slide
            in={isLoaded && currentPage === 'recovery-email-challenge'}
            speed={currentPage === 'recovery-email-challenge' ? 'slow' : undefined}
            timeout={currentPage === 'recovery-email-challenge' ? undefined : 150}
            direction="right"
            onEnter={() => setIsSliding(true)}
            onExited={() => setIsSliding(false)}
            alwaysRenderContent
        >
            <Box
                display="flex"
                flexDirection="column"
                gap={4}
                style={{ position: isSliding ? 'absolute' : 'static', width: '100%' }}
                mb={6}
            >
                {/* Loading state */}
                {loading && !codeSent && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        gap={2}
                        mt={9}
                    >
                        <ProgressCircle continuous />
                    </Box>
                )}

                {/* Error state when code could not be sent (e.g. no recovery email) */}
                {!loading && !codeSent && error && (
                    <Box display="flex" flexDirection="column" gap={4}>
                        <Box
                            p={4}
                            style={{
                                backgroundColor: '#AC145A',
                                borderRadius: '5px',
                                color: '#FFFFFF',
                            }}
                            role="alert"
                            aria-live="assertive"
                        >
                            {error}
                        </Box>
                        <Button
                            id="back-to-challenge-button"
                            size="lg"
                            onClick={() => setCurrentPage('challenge')}
                        >
                            {STRINGS.back || 'Back'}
                        </Button>
                    </Box>
                )}

                {/* Main challenge form */}
                {codeSent && (
                    <>
                        {codeAlreadyPending && (
                            <MFAErrorMessage error="code_already_pending" mb={4} />
                        )}
                        {error && (
                            <Box
                                p={4}
                                style={{
                                    backgroundColor: '#AC145A',
                                    borderRadius: '5px',
                                    color: '#FFFFFF',
                                }}
                                role="alert"
                                aria-live="assertive"
                            >
                                {error}
                            </Box>
                        )}

                        <Box>
                            <Typography variant="sectionTitleSm" weight="medium">
                                {STRINGS.enterOneTimeCode || 'Enter one-time code to continue'}
                            </Typography>
                            <Typography component="p" variant="bodySm" color="secondary" style={{ marginTop: '8px' }}>
                                {STRINGS.recoveryEmailChallengeDescription ||
                                 "If you don't have access to your current device, enter the one-time verification code sent to your recovery email address"} - {maskedEmail}
                            </Typography>
                        </Box>

                        <Input
                            value={code}
                            placeholder={STRINGS.enterCodeProvided || 'Enter the code provided'}
                            onChange={handleCodeChange}
                            id="recovery-challenge-code-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            stretched
                            aria-required="true"
                            autoFocus
                        />

                        {/* Resend Button */}
                        <Box style={{ textAlign: 'right' }}>
                            <Button
                                onClick={handleResendCode}
                                variant='text'
                                disabled={cooldown > 0 || resending}
                            >
                                <span style={{ color: '#007FAA' }}>
                                    {resending
                                        ? STRINGS.sending
                                        : cooldown > 0
                                        ? `${STRINGS.resend} (${cooldown}s)`
                                        : STRINGS.resend}
                                </span>
                            </Button>
                        </Box>

                        {/* Confirm button */}
                        <Button
                            id="confirm-recovery-code-button"
                            size="lg"
                            disabled={!isValidCode || loading}
                            onClick={handleVerifyCode}
                        >
                            {!loading ? (
                                STRINGS.confirm || 'Confirm'
                            ) : (
                                <ProgressCircle size='sm' continuous />
                            )}
                        </Button>
                    </>
                )}
            </Box>
        </Slide>
    );
};

RecoveryEmailChallenge.propTypes = {
    isLoaded: PropTypes.bool.isRequired,
    currentPage: PropTypes.string.isRequired,
    isSliding: PropTypes.bool.isRequired,
    setIsSliding: PropTypes.func.isRequired,
    setCurrentPage: PropTypes.func.isRequired,
    handleError: PropTypes.func,
    nextUrl: PropTypes.string,
};

RecoveryEmailChallenge.defaultProps = {
    handleError: () => {},
    nextUrl: '',
};

export default RecoveryEmailChallenge;
